import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import Today from './Today';
import Diary from './Diary';
import Settings from './Settings';
import Students from './Students';
import Waitlist from './Waitlist';
import Home from './Home';
import GoStudents from './GoStudents';

// Mobile breakpoint — matches Students.js. Used to trim the nav tab font
// slightly so all five labels stay on one row on narrow phones.
const MOBILE_MAX = 640;
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false);
  useEffect(() => {
    const onR = () => setM(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return m;
}

/**
 * Cockpit — the instructor's control surface.
 *
 * Nav:
 *   Today    → today's lessons at a glance — LIVE
 *   Diary    → the week, lesson detail + cancel/move/deposit — LIVE
 *   Students → student list + approval queue + per-student EDT panel — LIVE
 *   Waitlist → leads captured when full; accept one to invite them in — LIVE
 *   Settings → the control levers (capacity, booking rules, deposits) — LIVE
 *
 * Data here is scoped to THIS instructor by RLS at the database level.
 *
 * REBRAND: shell moved onto the teal brand (was purple/grey). Tokens match
 * the tab content files and visionry.io.
 * MOBILE FIX: the nav tabs now SHARE the row width (flex:1 each, no fixed
 * padding that overflows) and shrink their text/padding so all five fit on a
 * ~390px phone screen — no horizontal overflow, so the page no longer zooms
 * out. Header also allowed to wrap/shrink so "Sign out" never gets pushed off.
 */

// ── Brand tokens (match visionry.io / the tab content files) ──
const C = {
  bg:      '#061618',
  header:  '#0d3338',
  border:  '#10434a',
  teal:    '#25a2b3',
  text:    '#e0f0f2',
  muted:   '#7fb8c0',
  mutedDim:'#3d5a5f'
};

export default function Cockpit({ instructor, session }) {
  // ── THE WALL ──
  // mode 'waitlist' = demand-management-only instructors. Their cockpit is the
  // Waitlist (the product) plus Settings — Today/Diary/Students are full-mode
  // surfaces for diaries that live in Visionry, which theirs doesn't (yet).
  // WHY trim rather than fork: one dashboard, one flag, instantly reversible
  // when an instructor upgrades to full.
  const isWaitlistMode = instructor.mode === 'waitlist';
  // Fill (calendar cancellation sync): same roster-based Home/Students as
  // waitlist mode, but no Waitlist tab/product at all — Fill offers to
  // students only.
  const isFillMode = instructor.mode === 'fill';
  // Both non-full modes land on Home and use the roster-backed Students tab.
  const usesRosterStudents = isWaitlistMode || isFillMode;
  // ?tab=settings lets an external redirect (e.g. back from Stripe Connect
  // onboarding) land on a specific tab — navigation is otherwise plain React
  // state with no URL reflection, so without this a full-page redirect back
  // would always drop the instructor on Home/Today regardless of intent.
  const [active, setActive] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return requested || (usesRosterStudents ? 'home' : 'today');
  });
  const isMobile = useIsMobile();
  const displayName = instructor.school_name || instructor.instructor_name || 'Your school';

  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  const NAV = isFillMode
    ? [
        { id: 'home',     label: 'Home' },
        { id: 'students', label: 'Students' },
        { id: 'settings', label: 'Settings' }
      ]
    : isWaitlistMode
    ? [
        { id: 'home',     label: 'Home' },
        { id: 'waitlist', label: 'Waitlist' },
        { id: 'students', label: 'Students' },
        { id: 'settings', label: 'Settings' }
      ]
    : [
        { id: 'today',    label: 'Today' },
        { id: 'diary',    label: 'Diary' },
        { id: 'students', label: 'Students' },
        { id: 'waitlist', label: 'Waitlist' },
        { id: 'settings', label: 'Settings' }
      ];

  return (
    <div style={{
      minHeight: '100vh', background: C.bg,
      fontFamily: 'Poppins, sans-serif', color: C.text,
      overflowX: 'hidden'   // safety net: nothing widens the page
    }}>
      {/* Header */}
      <div style={{
        background: C.header, borderBottom: `1px solid ${C.border}`,
        padding: '14px 16px', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 100
      }}>
        <div style={{ minWidth: 0 }}>
          {/* minWidth:0 lets a long school name truncate instead of pushing
              the Sign out button off the right edge */}
          <div style={{
            fontSize: 17, fontWeight: 700,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          }}>
            {displayName}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            {instructor.instructor_key} · Instructor cockpit
          </div>
        </div>
        <button
          onClick={handleSignOut}
          style={{
            flexShrink: 0,   // never let the button get squeezed away
            background: 'none', border: `1px solid ${C.border}`, borderRadius: 8,
            color: C.muted, fontSize: 13, padding: '6px 12px', cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          Sign out
        </button>
      </div>

      {/* Nav tabs — share the row equally, always one row (five fit any width) */}
      <div style={{
        background: C.header, borderBottom: `1px solid ${C.border}`,
        display: 'flex'
      }}>
        {NAV.map(n => (
          <button
            key={n.id}
            onClick={() => setActive(n.id)}
            style={{
              flex: 1,                 // share width equally → always fits
              minWidth: 0,             // allow shrinking below content width
              minHeight: 40,
              background: 'none', border: 'none',
              borderBottom: active === n.id ? `2px solid ${C.teal}` : '2px solid transparent',
              color: active === n.id ? C.text : C.muted,
              fontSize: isMobile ? 13 : 14,
              padding: '10px 6px',
              cursor: 'pointer',
              fontWeight: active === n.id ? 600 : 400,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
            }}
          >
            {n.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '20px', maxWidth: 800, margin: '0 auto' }}>
        {active === 'home'     && <Home instructor={instructor} onNavigate={setActive} />}
        {active === 'today'    && <Today instructor={instructor} onNavigate={setActive} />}
        {active === 'diary'    && <Diary instructor={instructor} />}
        {active === 'students' && (usesRosterStudents
          ? <GoStudents instructor={instructor} onNavigate={setActive} />
          : <Students instructor={instructor} />)}
        {active === 'waitlist' && <Waitlist instructor={instructor} />}
        {active === 'settings' && <Settings instructor={instructor} />}
      </div>
    </div>
  );
}